// import { supabase } from '@/lib/supabase';
import type { Database } from '@/types/supabase';
import type { PoemHighlight, PoemHighlightInsert, PoemHighlightUpdate } from '@/types/highlights';
import { handleError, logError, ErrorType, AppError } from '@/utils/error/errorHandler';
import { withRetry } from '@/utils/error/retry';
import { validateUUID } from '@/utils/validationWrapper';
import { CacheEntityType, CACHE_TTL } from '@/utils/cache/cacheConfig';
import { cacheManager } from '@/utils/cache/cacheManager';
import { invalidateEntityById, invalidateRelatedCaches } from '@/utils/cache/cacheInvalidation';
import { ApiProvider } from '@/types/sharedTypes';
import { getHighlightLogger } from '@/utils/logging/browserLogger';
import type { HighlightLogContext } from '@/utils/logging/browserLogger';
import { buildHighlightPrompt } from '@/utils/highlightPromptBuilder';
import { 
  generateHighlightsWithClaude, 
  generateHighlightsWithGPT,
} from '@/services/highlightGenerationService';
import { 
  validateStylePoints as validateStylePointsUnified,
  validateHighlightGenerationInput
} from '@/utils/highlightValidationUnified';
import { fetchWithAuth } from '@/lib/apiUtils';
// import { supabaseAdminClient } from '@/lib/supabase';
// import { 
//   getActiveHighlightPromptTemplateV2,
//   type PoemPromptTemplate
// } from './promptTemplateService';

// Define the interface locally since it's not exported from highlightPromptBuilder
interface HighlightPromptOptions {
  forceHighlightCount?: number;
  includeStyleGuide?: boolean;
  temperature?: number;
  [key: string]: any; // Allow additional properties
}

// Remove DB type - rely on types defined in @/types/highlights
// type DatabasePoemHighlight = Database[\'public\'][\'Tables\'][\'poem_highlights\'][\'Row\'];

// Cache keys for highlights
const HIGHLIGHT_CACHE_KEYS = {
  HIGHLIGHTS_BY_POEM: 'highlights:poem:',
};

/**
 * Get highlights for a specific poem by calling the backend API
 * @param poemId - The poem's UUID
 * @returns Array of highlight objects
 */
export async function getHighlightsByPoemId(poemId: string): Promise<PoemHighlight[]> {
  validateUUID(poemId, 'poemId');
  
  // Check cache first
  const cacheKey = `${HIGHLIGHT_CACHE_KEYS.HIGHLIGHTS_BY_POEM}${poemId}`;
  const cachedHighlights = cacheManager.get<PoemHighlight[]>(cacheKey);
  if (cachedHighlights) {
    console.log(`highlightService: getHighlightsByPoemId returning cached data for poem ${poemId}`);
    return cachedHighlights;
  }
  
  try {
    console.log(`highlightService: getHighlightsByPoemId fetching from API for poem ${poemId}`);
    const apiUrl = `/api/highlights/poem/${poemId}`;
    const response = await fetchWithAuth(apiUrl);

    if (!response.ok) {
      let errorBody = {};
      try { errorBody = await response.json(); } catch(e) {/* ignore */}
      console.error('highlightService: getHighlightsByPoemId API error', {
        status: response.status,
        statusText: response.statusText,
        errorBody,
        url: apiUrl
      });
      // Map 404 to NOT_FOUND, others to NETWORK
      const errorType = response.status === 404 ? ErrorType.NOT_FOUND : ErrorType.NETWORK;
      throw new AppError({
        message: `API Error fetching highlights for poem ${poemId}: ${response.statusText} (Status: ${response.status})`,
        type: errorType,
        statusCode: response.status,
        metadata: { url: apiUrl, responseBody: errorBody, poemId }
      });
    }

    const highlights: PoemHighlight[] = await response.json();
    console.log(`highlightService: getHighlightsByPoemId fetched ${highlights.length} highlights from API for poem ${poemId}`);
    
    // Cache the result
    cacheManager.set(cacheKey, highlights, CACHE_TTL.MEDIUM);
    
    return highlights;
  } catch (error) {
    // Handle validation errors specifically
    if (error instanceof AppError && error.type === ErrorType.VALIDATION) {
      logError(error, `getHighlightsByPoemId(${poemId}) - validation`);
      throw error;
    }
    // Handle other errors (API, unexpected) using handleError
    const appError = handleError(error, `getHighlightsByPoemId(${poemId})`);
    logError(appError, `getHighlightsByPoemId(${poemId})`);
    
    // For NOT_FOUND errors, return an empty array
    if (appError.type === ErrorType.NOT_FOUND) {
      console.log(`highlightService: getHighlightsByPoemId returning empty array due to NOT_FOUND for poem ${poemId}`);
      return []; 
    }
    
    throw appError;
  }
}

/**
 * Create a new highlight for a poem by calling the backend API
 * @param highlight - The highlight object to create
 * @returns The created highlight
 */
export async function createHighlight(highlight: PoemHighlightInsert): Promise<PoemHighlight> {
  try {
    // Basic validation - API will do more thorough validation
    validateUUID(highlight.poem_id, 'poem_id');
    if (!highlight.text || !highlight.note) { 
      throw new AppError('Missing required highlight fields', ErrorType.VALIDATION); 
    }

    console.log('highlightService: createHighlight calling API', { poemId: highlight.poem_id });
    const apiUrl = `/api/highlights`;
    const response = await fetchWithAuth(apiUrl, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(highlight)
    });

    if (!response.ok) {
      let errorBody = {};
      try { errorBody = await response.json(); } catch(e) {/* ignore */}
      console.error('highlightService: createHighlight API error', { 
        status: response.status, 
        statusText: response.statusText, 
        errorBody, 
        url: apiUrl 
      });
      const errorType = response.status === 400 ? ErrorType.VALIDATION : ErrorType.NETWORK;
      throw new AppError({
        message: `API Error creating highlight: ${response.statusText} (Status: ${response.status})`,
        type: errorType,
        statusCode: response.status,
        metadata: { url: apiUrl, requestBody: highlight, responseBody: errorBody }
      });
    }

    const createdHighlight: PoemHighlight = await response.json();
    console.log('highlightService: createHighlight successful', { highlightId: createdHighlight.id });
    
    // Invalidate cache for the poem
    invalidateHighlightCacheByPoemId(createdHighlight.poem_id);
    
    return createdHighlight;
  } catch (error) {
    const appError = handleError(error, 'createHighlight', { highlightData: highlight });
    logError(appError, 'createHighlight');
    throw appError;
  }
}

/**
 * Create multiple highlights for a poem in a batch by calling the backend API
 * @param highlights - Array of highlight objects to create
 * @returns Array of created highlights
 */
export async function createHighlightsBatch(highlights: PoemHighlightInsert[]): Promise<PoemHighlight[]> {
  if (!highlights || !Array.isArray(highlights) || highlights.length === 0) {
    console.log('highlightService: createHighlightsBatch called with empty array, returning empty');
    return [];
  }
  
  try {
    // Basic validation
    const poemId = highlights[0].poem_id;
    validateUUID(poemId, 'poem_id');

    console.log(`highlightService: createHighlightsBatch calling API for poem ${poemId}`, { count: highlights.length });
    const apiUrl = `/api/highlights/batch`;
    const response = await fetchWithAuth(apiUrl, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(highlights)
    });

    if (!response.ok) {
      let errorBody = {};
      try { errorBody = await response.json(); } catch(e) {/* ignore */}
      console.error('highlightService: createHighlightsBatch API error', { 
        status: response.status, 
        statusText: response.statusText, 
        errorBody, 
        url: apiUrl 
      });
      const errorType = response.status === 400 ? ErrorType.VALIDATION : ErrorType.NETWORK;
      throw new AppError({
        message: `API Error creating highlights batch: ${response.statusText} (Status: ${response.status})`,
        type: errorType,
        statusCode: response.status,
        metadata: { url: apiUrl, requestBodyCount: highlights.length, responseBody: errorBody }
      });
    }

    const createdHighlights: PoemHighlight[] = await response.json();
    console.log(`highlightService: createHighlightsBatch successful, created ${createdHighlights.length} highlights`);
    
    // Invalidate cache for the affected poem
    invalidateHighlightCacheByPoemId(poemId);
    
    return createdHighlights;
  } catch (error) {
    const appError = handleError(error, 'createHighlightsBatch', { highlightCount: highlights.length });
    logError(appError, 'createHighlightsBatch');
    throw appError;
  }
}

/**
 * Update an existing highlight by calling the backend API
 * @param id - The highlight's UUID
 * @param updates - The highlight fields to update
 * @returns The updated highlight
 */
export async function updateHighlight(id: string, updates: PoemHighlightUpdate): Promise<PoemHighlight> {
  try {
    validateUUID(id, 'id');
    // Basic validation on updates
    if (!updates || typeof updates !== 'object' || Object.keys(updates).length === 0) {
      throw new AppError('Invalid update data', ErrorType.VALIDATION);
    }

    console.log(`highlightService: updateHighlight calling API for ID ${id}`);
    const apiUrl = `/api/highlights/${id}`;
    const response = await fetchWithAuth(apiUrl, {
      method: 'PUT',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(updates)
    });

    if (!response.ok) {
      let errorBody = {};
      try { errorBody = await response.json(); } catch(e) {/* ignore */}
      console.error('highlightService: updateHighlight API error', { 
        status: response.status, 
        statusText: response.statusText, 
        errorBody, 
        url: apiUrl 
      });
      const errorType = response.status === 404 ? ErrorType.NOT_FOUND : (response.status === 400 ? ErrorType.VALIDATION : ErrorType.NETWORK);
      throw new AppError({
        message: `API Error updating highlight ${id}: ${response.statusText} (Status: ${response.status})`,
        type: errorType,
        statusCode: response.status,
        metadata: { url: apiUrl, requestBody: updates, responseBody: errorBody, highlightId: id }
      });
    }
    
    const updatedHighlight: PoemHighlight = await response.json();
    console.log('highlightService: updateHighlight successful', { highlightId: updatedHighlight.id });
    
    // Invalidate cache for the poem associated with the highlight
    invalidateHighlightCacheByPoemId(updatedHighlight.poem_id);
    // If the poem_id itself was updated, invalidate the old one too (though unlikely)
    // This requires knowing the old poem_id, which we don't fetch anymore.
    // Relying on the API response having the correct (potentially updated) poem_id.
    
    return updatedHighlight;
  } catch (error) {
    const appError = handleError(error, 'updateHighlight', { highlightId: id, updates });
    logError(appError, 'updateHighlight');
    throw appError;
  }
}

/**
 * Delete a highlight by calling the backend API
 * @param id - The highlight's UUID
 */
export async function deleteHighlight(id: string): Promise<void> {
  let poemIdToInvalidate: string | null = null;
  try {
    validateUUID(id, 'id');
    
    // OPTIONAL: Pre-fetch highlight to get poem_id for cache invalidation before delete?
    // This adds an extra API call. Alternatively, the API could return the deleted highlight info.
    // For now, we'll attempt invalidation based on assumption or skip it if it fails.
    
    console.log(`highlightService: deleteHighlight calling API for ID ${id}`);
    const apiUrl = `/api/highlights/${id}`;
    const response = await fetchWithAuth(apiUrl, {
      method: 'DELETE'
    });

    if (!response.ok) {
      // 204 No Content is success for DELETE
      if (response.status === 204) {
        console.log(`highlightService: deleteHighlight successful (204 No Content) for ID ${id}`);
        // Attempt cache invalidation here if we knew the poemId
        // Since we don't, we might skip invalidation or use a broader strategy
        return;
      }
      
      let errorBody = {};
      // Don't parse JSON for 204
      if (response.status !== 204) { 
        try { errorBody = await response.json(); } catch(e) {/* ignore */} 
      }
      console.error('highlightService: deleteHighlight API error', { 
        status: response.status, 
        statusText: response.statusText, 
        errorBody, 
        url: apiUrl 
      });
      const errorType = response.status === 404 ? ErrorType.NOT_FOUND : ErrorType.NETWORK;
      throw new AppError({
        message: `API Error deleting highlight ${id}: ${response.statusText} (Status: ${response.status})`,
        type: errorType,
        statusCode: response.status,
        metadata: { url: apiUrl, responseBody: errorBody, highlightId: id }
      });
    }
    
    // If response.ok and not 204 (e.g., 200 OK with body), handle potential success response
    console.log(`highlightService: deleteHighlight successful (Status: ${response.status}) for ID ${id}`);
    // Attempt cache invalidation based on potential response body or skip

  } catch (error) {
    const appError = handleError(error, 'deleteHighlight', { highlightId: id });
    logError(appError, 'deleteHighlight');
    throw appError;
  }
}

/**
 * Invalidate highlights cache for a specific poem
 * @param poemId - The poem's UUID
 */
export function invalidateHighlightCacheByPoemId(poemId: string): void {
  const cacheKey = `${HIGHLIGHT_CACHE_KEYS.HIGHLIGHTS_BY_POEM}${poemId}`;
  console.log(`highlightService: Invalidating cache key: ${cacheKey}`);
  cacheManager.delete(cacheKey);
  // Also invalidate related poem cache since highlights might be embedded
  // invalidateRelatedCaches(CacheEntityType.POEM, poemId); // Keep this?
}

// Remove findAllOccurrences as it's likely unused or backend logic
// Remove generateHighlightsForPoem as it's unused / superseded
// Remove getPoetNameById as it uses Supabase directly

/**
 * Generate and save highlights for a poem using the unified backend API endpoint.
 * Handles calling the highlight generation service.
 * @param poemId - The poem's UUID
 * @param content - The poem content as an array of lines
 * @param poetName - The name of the poet
 * @param options - Highlight generation options
 * @param provider - The AI provider to use (OpenAI or Anthropic)
 * @param context - Logging context
 * @param runId - Optional run ID
 * @returns Array of created highlights
 */
export async function generateAndSaveHighlightsUnified(
  poemId: string,
  content: string[],
  poetName: string,
  options: HighlightPromptOptions,
  provider: ApiProvider,
  context: HighlightLogContext = 'admin',
  runId?: string | null
): Promise<PoemHighlight[]> {
  const logger = getHighlightLogger(context);
  logger.info('generateAndSaveHighlightsUnified started', { poemId, poetName, provider, options });

  try {
    // Basic input validation (API endpoint will do more)
    validateUUID(poemId, 'poemId');
    if (!content || content.length === 0) throw new AppError('Poem content is required', ErrorType.VALIDATION);
    if (!poetName) throw new AppError('Poet name is required', ErrorType.VALIDATION);

    // --- REMOVE Fetching Active Highlight Prompts --- 
    // logger.info('Fetching active highlight prompts...');
    // let systemPromptText: string | null | undefined = null;
    // let userPromptText: string | null | undefined = null;
    // try { ... } catch { ... }
    // --- END REMOVE Fetching Prompts ---

    const apiUrl = `/api/highlights/generate`;
    // Prepare body
    const requestBody: any = {
      poemId,
      poemContent: content,
      poetName,
      provider,
      options,
    };
    if (runId) requestBody.runId = runId;

    // --- ADDED: Log full payload before fetch --- 
    console.log(`[highlightService] Sending ${apiUrl} payload:`, JSON.stringify(requestBody, null, 2)); 

    logger.info('Calling backend API to generate highlights', { apiUrl, provider });
    const response = await fetchWithAuth(apiUrl, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(requestBody)
    });

    if (!response.ok) {
      let errorBody = {};
      try { errorBody = await response.json(); } catch(e) {/* ignore */}
      logger.error('Backend API error during highlight generation', {
        status: response.status,
        statusText: response.statusText,
        errorBody,
        url: apiUrl,
        poemId,
        provider
      });
      const errorType = response.status === 400 ? ErrorType.VALIDATION : ErrorType.NETWORK;
      throw new AppError({
        message: `API Error generating highlights: ${response.statusText} (Status: ${response.status})`,
        type: errorType,
        statusCode: response.status,
        metadata: { url: apiUrl, requestBody, responseBody: errorBody }
      });
    }

    const generatedHighlights: PoemHighlight[] = await response.json();
    logger.info(`Successfully generated ${generatedHighlights.length} highlights via backend API`, { poemId });

    // Cache invalidation (assuming the API endpoint handles saving, 
    // but frontend cache needs update)
    invalidateHighlightCacheByPoemId(poemId);

    if (generatedHighlights && generatedHighlights.length > 0) {
      logger.info(`Generated ${generatedHighlights.length} highlights. Preparing batch insert.`);

      // Front‑end no longer writes directly to Supabase; assume backend saved them.
      return generatedHighlights;
    } else {
      logger.warning('No highlights were generated or returned by AI service.', { poemId, runId });
      return [];
    }
  } catch (error) {
    logger.error('Error in generateAndSaveHighlightsUnified', error, { poemId });
    const appError = handleError(error, 'generateAndSaveHighlightsUnified', { poemId, poetName, provider, runId });
    // Don't log again, handleError does it.
    throw appError;
  }
}

// Remove deprecated generateAndSaveHighlightsWithChatGPT
// Remove deprecated generateAndSaveHighlightsWithClaude
// Remove deprecated generateAndSaveHighlights

/**
 * Generates highlights for a poem using AI API and saves them to the database
 * @deprecated Use generateAndSaveHighlightsUnified instead
 * @param poemId - The ID of the poem
 * @param content - The poem content as an array of lines
 * @param poetName - The name of the poet whose style is being emulated
 * @param options - Options to customize the highlight generation
 * @param provider - The API provider to use (OpenAI or Anthropic)
 * @param context - The context of the highlight generation (admin or generate)
 * @returns The generated and saved highlights
 */
export async function generateAndSaveHighlightsWithAI(
  poemId: string,
  content: string[],
  poetName: string,
  options: HighlightPromptOptions = {},
  provider: ApiProvider = ApiProvider.ANTHROPIC, // Default to Anthropic
  context: HighlightLogContext = 'admin' // Default to admin context
): Promise<PoemHighlight[]> {
  // Simply forward to the unified function
  return generateAndSaveHighlightsUnified(
    poemId,
    content,
    poetName,
    options,
    provider,
    context
  );
} 